<template>
  <div class="bg-gradient-to-b from-gray-900 to-blue-900 min-h-screen w-full">
    <div class="px-4 py-8 mx-auto container">
      <!-- Naslov stranice -->
      <h1
        class="text-3xl font-bold text-white mb-6 border-b border-blue-500 pb-2 inline-block"
      >
        Moj profil
      </h1>

      <!-- Loader -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"
        ></div>
      </div>

      <!-- Error message -->
      <div
        v-else-if="error"
        class="bg-red-800 text-white p-6 rounded-lg mb-6 shadow-lg border-l-4 border-red-500"
      >
        <div class="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 mr-3 text-red-300 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span class="break-words min-w-0">{{ error }}</span>
        </div>
      </div>

      <!-- Profil content -->
      <div
        v-else-if="user"
        class="bg-gray-800 rounded-lg shadow-xl overflow-hidden border border-blue-500"
      >
        <!-- Profil header -->
        <div class="bg-gradient-to-r from-blue-800 to-blue-600 p-6">
          <div class="flex items-center space-x-4">
            <!-- Avatar (placeholder) -->
            <div
              class="bg-blue-700 rounded-full h-20 w-20 sm:h-24 sm:w-24 flex items-center justify-center text-2xl sm:text-3xl text-white font-bold shadow-lg border-2 border-blue-300 flex-shrink-0"
            >
              {{ userInitials }}
            </div>

            <!-- Osnovne informacije -->
            <div class="ml-2 min-w-0 flex-1">
              <h2 class="text-2xl sm:text-3xl font-bold text-white break-words">
                {{ user.username }}
              </h2>
              <p class="text-blue-200 text-base sm:text-lg break-words">
                {{ user.firstName }} {{ user.lastName }}
              </p>
              <p v-if="user.city" class="text-blue-100 mt-1 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 mr-1 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="break-words min-w-0">{{ user.city }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Profil details -->
        <div class="p-4 sm:p-8 bg-gradient-to-b from-gray-800 to-gray-900">
          <!-- Informacije o korisniku -->
          <div
            class="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg border border-blue-400 mb-6"
          >
            <h3
              class="text-lg sm:text-xl font-semibold text-white mb-6 border-b border-blue-400 pb-2 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2 text-blue-400 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="break-words min-w-0">Informacije o profilu</span>
            </h3>
            <div class="space-y-6">
              <div
                class="flex flex-col sm:flex-row border-b border-gray-600 pb-4 transition-all duration-200 hover:bg-gray-700 p-2 rounded-md"
              >
                <span
                  class="text-blue-300 sm:w-32 font-medium mb-1 sm:mb-0 flex-shrink-0"
                  >ID korisnika:</span
                >
                <span class="text-white break-all min-w-0">{{ user.id }}</span>
              </div>
              <div
                class="flex flex-col sm:flex-row border-b border-gray-600 pb-4 transition-all duration-200 hover:bg-gray-700 p-2 rounded-md"
              >
                <span
                  class="text-blue-300 sm:w-32 font-medium mb-1 sm:mb-0 flex-shrink-0"
                  >Korisničko ime:</span
                >
                <span class="text-white break-words min-w-0">{{
                  user.username
                }}</span>
              </div>
              <div
                class="flex flex-col sm:flex-row border-b border-gray-600 pb-4 transition-all duration-200 hover:bg-gray-700 p-2 rounded-md"
              >
                <span
                  class="text-blue-300 sm:w-32 font-medium mb-1 sm:mb-0 flex-shrink-0"
                  >Ime:</span
                >
                <span class="text-white break-words min-w-0">{{
                  user.firstName || 'Nije uneto'
                }}</span>
              </div>
              <div
                class="flex flex-col sm:flex-row border-b border-gray-600 pb-4 transition-all duration-200 hover:bg-gray-700 p-2 rounded-md"
              >
                <span
                  class="text-blue-300 sm:w-32 font-medium mb-1 sm:mb-0 flex-shrink-0"
                  >Prezime:</span
                >
                <span class="text-white break-words min-w-0">{{
                  user.lastName || 'Nije uneto'
                }}</span>
              </div>
              <div
                class="flex flex-col sm:flex-row border-b border-gray-600 pb-4 transition-all duration-200 hover:bg-gray-700 p-2 rounded-md"
              >
                <span
                  class="text-blue-300 sm:w-32 font-medium mb-1 sm:mb-0 flex-shrink-0"
                  >Email:</span
                >
                <span class="text-white break-all min-w-0">{{
                  user.email
                }}</span>
              </div>
              <div
                class="flex flex-col sm:flex-row transition-all duration-200 hover:bg-gray-700 p-2 rounded-md"
              >
                <span
                  class="text-blue-300 sm:w-32 font-medium mb-1 sm:mb-0 flex-shrink-0"
                  >Grad:</span
                >
                <span class="text-white break-words min-w-0">{{
                  user.city || 'Nije unet'
                }}</span>
              </div>
            </div>
          </div>

          <!-- Danger Zone - Account Deletion -->
          <div
            class="bg-red-900 bg-opacity-20 p-4 sm:p-6 rounded-lg shadow-lg border border-red-500 border-opacity-50"
          >
            <h3
              class="text-lg sm:text-xl font-semibold text-red-400 mb-4 border-b border-red-500 border-opacity-50 pb-2 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 mr-2 text-red-400 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span class="break-words min-w-0">Opasna zona</span>
            </h3>
            <div class="space-y-4">
              <div>
                <p class="text-red-300 text-sm mb-4">
                  Brisanje naloga je nepovratna akcija. Svi vaši podaci,
                  proizvodi, poruke i ocene će biti trajno obrisani.
                </p>
                <button
                  @click="showDeleteConfirmation"
                  :disabled="deleteLoading"
                  class="bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors shadow-lg text-sm sm:text-base font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    v-if="deleteLoading"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  {{ deleteLoading ? 'Brisanje...' : 'Obriši nalog' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state ako nema podataka -->
      <div
        v-else
        class="bg-gray-800 p-4 sm:p-8 rounded-lg shadow-lg text-center border border-blue-500"
      >
        <div
          class="bg-blue-900 w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-10 w-10 sm:h-16 sm:w-16 text-blue-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </div>
        <h3 class="text-xl sm:text-2xl font-medium text-white mt-4">
          Podaci o profilu nisu dostupni
        </h3>
        <p class="text-blue-300 mt-2 mb-6 break-words">
          Pokušajte ponovo kasnije ili se odjavite pa prijavite
        </p>
        <button
          class="bg-blue-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-md hover:bg-blue-500 transition-colors shadow-lg text-sm sm:text-base"
          @click="fetchUserProfile"
        >
          Pokušaj ponovo
        </button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div
        class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
      ></div>
      <div
        class="bg-gray-800 text-white rounded-lg shadow-xl border border-red-500 p-6 max-w-md z-10 mx-4"
      >
        <div class="flex items-start mb-4">
          <div class="flex-shrink-0 mr-4">
            <svg
              class="h-8 w-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-medium text-red-400 mb-2">
              Potvrda brisanja naloga
            </h3>
            <p class="text-gray-300 text-sm">
              Da li ste sigurni da želite da obrišete svoj nalog? Ova akcija je
              nepovratna i svi vaši podaci će biti trajno obrisani.
            </p>
          </div>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-gray-300 hover:text-white transition-colors"
          >
            Otkaži
          </button>
          <button
            @click="confirmDelete"
            :disabled="deleteLoading"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <svg
              v-if="deleteLoading"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ deleteLoading ? 'Brisanje...' : 'Da, obriši nalog' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import ToastNotification from '@/components/ToastNotification.vue';
import api from '@/services/api';
import { User } from '@/types/auth-types';

export default defineComponent({
  name: 'AccountView',
  components: {
    ToastNotification,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const user = ref<User | null>(null);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const deleteLoading = ref(false);
    const showDeleteModal = ref(false);
    const toast = ref({
      show: false,
      message: '',
      type: 'success', // success, error, warning, info
    });

    // Izračunava inicijale korisnika za avatar
    const userInitials = computed(() => {
      if (!user.value) return '';

      const firstName = user.value.firstName || '';
      const lastName = user.value.lastName || '';

      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
      } else if (user.value.username) {
        return user.value.username.substring(0, 2).toUpperCase();
      }

      return '';
    });

    // Show toast notification
    const showToast = (
      message: string,
      type: 'success' | 'error' | 'warning' | 'info' = 'success'
    ) => {
      toast.value = {
        show: true,
        message,
        type,
      };
    };

    // Učitavanje podataka o korisniku
    const fetchUserProfile = async () => {
      loading.value = true;
      error.value = null;

      try {
        // Prvo pokušavamo da dobijemo korisnika iz store-a
        const storeUser = store.getters['auth/currentUser'];

        if (storeUser) {
          // Ako imamo korisnika u store-u, koristimo ga
          user.value = storeUser;
          loading.value = false;
          return;
        }

        // Ako nemamo korisnika u store-u, pokušavamo da ga dobijemo sa API-ja
        try {
          await store.dispatch('auth/fetchCurrentUser');
          user.value = store.getters['auth/currentUser'];

          if (!user.value) {
            // Ako i dalje nemamo korisnika, prikažimo grešku
            error.value =
              'Nije moguće učitati podatke o korisniku. Proverite da li ste prijavljeni.';
          }
        } catch (err) {
          console.error('Error fetching current user:', err);
          error.value = 'Greška prilikom učitavanja podataka o korisniku.';
        }
      } catch (err) {
        console.error('Error in account view:', err);
        error.value = 'Greška prilikom učitavanja podataka o profilu.';
      } finally {
        loading.value = false;
      }
    };

    // Show delete confirmation modal
    const showDeleteConfirmation = () => {
      showDeleteModal.value = true;
    };

    // Cancel delete action
    const cancelDelete = () => {
      showDeleteModal.value = false;
    };

    // Confirm delete action
    const confirmDelete = async () => {
      if (!user.value) return;

      deleteLoading.value = true;

      try {
        // Call the API to delete the user account
        await api.delete(`/api/users/${user.value.id}`);

        // Show success message
        showToast('Vaš nalog je uspešno obrisan.', 'success');

        // Clear user data from store
        store.dispatch('auth/logout');

        // Redirect to home page after a short delay
        setTimeout(() => {
          router.push('/');
        }, 2000);
      } catch (err: any) {
        console.error('Error deleting account:', err);

        // Handle different types of errors
        if (err?.response?.status === 403) {
          showToast('Nemate dozvolu za brisanje ovog naloga.', 'error');
        } else if (err?.response?.status === 404) {
          showToast('Nalog nije pronađen.', 'error');
        } else if (err?.response?.status === 429) {
          showToast('Previše zahteva. Pokušajte ponovo kasnije.', 'warning');
        } else {
          showToast(
            'Greška prilikom brisanja naloga. Pokušajte ponovo.',
            'error'
          );
        }
      } finally {
        deleteLoading.value = false;
        showDeleteModal.value = false;
      }
    };

    // Inicijalno učitavanje
    onMounted(() => {
      fetchUserProfile();
    });

    return {
      user,
      loading,
      error,
      deleteLoading,
      showDeleteModal,
      toast,
      userInitials,
      fetchUserProfile,
      showDeleteConfirmation,
      cancelDelete,
      confirmDelete,
    };
  },
});
</script>
